import { useQuery } from '@tanstack/react-query';
import { FC, MouseEvent, useEffect, useState } from 'react';
import {
  Control,
  Controller,
  UseFormSetValue,
  useWatch,
} from 'react-hook-form';
import styled, { css } from 'styled-components';

import { Input, Label } from '@/components/Forms';
import InfoTooltip from '@/components/InfoTooltip';
import Text from '@/components/Text';
import useDebounce from '@/hooks/useDebounce';
import {
  BankMark,
  PayoneerMark,
  PaypalMark,
} from '@/pages/InvoiceList/components/PaymentMethodIcon';
import { InvoicesMemberResource } from '@/types/resources';
import { api } from '@/utils/api';
import { getMemberPaymentMethod, paymentMethods } from '@/utils/data/payments';
import { PaymentMethod } from '@/utils/enums';

const InputWrap = styled.div`
  position: relative;
  ${Input} {
    padding-right: 65px;
  }
`;

export const Tags = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin-top: 16px;
`;

const Autocomplete = styled.div`
  position: absolute;
  top: calc(100% + 8px);
  background-color: ${({ theme }) => theme.colors.white};
  box-shadow: 0px 0px 8px rgba(37, 40, 43, 0.12);
  border-radius: 4px;
  padding: 8px 4px;
  max-height: 368px;
  overflow-y: auto;
  left: 0;
  width: 100%;
  z-index: 1;
`;

const AutocompleteButton = styled.button`
  padding: 9px 12px;
  background-color: ${({ theme }) => theme.colors.white};
  transition: 0.2s ease-out all;
  line-height: 22px;
  width: 100%;
  text-align: left;
  border-radius: 4px;
  outline: none;
  display: flex;
  align-items: center;
  svg {
    margin-right: 10px;
  }
  &:hover,
  &:focus {
    background-color: ${({ theme }) => theme.colors.grayscaleLight};
    ${Text} {
      &:first-child {
        color: ${({ theme }) => theme.colors.black};
      }
    }
  }
  ${Text} {
    transition: color ease-out 0.2s;
    > span {
      color: ${({ theme }) => theme.colors.purple};
    }
  }
`;

const AttachmentBox = styled.div<{
  $isGray?: boolean;
}>`
  ${({ $isGray }) =>
    $isGray &&
    css`
      margin: 32px 0;
    `}
`;

// The wallets a member could be paid through, kept to what the form still
// offers — a member whose only wallet is no longer offered falls back to bank.
const memberWallets = (
  member: InvoicesMemberResource | undefined,
  methods: PaymentMethod[],
) =>
  [
    ...(member?.paypal_email ? [PaymentMethod.Paypal] : []),
    ...(member?.payoneer_email ? [PaymentMethod.Payoneer] : []),
  ].filter((method) => methods.includes(method));

interface Props {
  control: Control<any>;
  name: string;
  setValue?: UseFormSetValue<any>;
  setShouldDesableType?: React.Dispatch<React.SetStateAction<boolean>>;
  onMemberMatch?: (member: InvoicesMemberResource | null) => void;
  label?: string;
  // Which methods the surrounding form offers — narrower than `paymentMethods`
  // where a method is no longer accepted for new invoices.
  methods?: PaymentMethod[];
}

const InvoicesRecieverField: FC<Props> = ({
  control,
  name,
  setValue,
  setShouldDesableType,
  onMemberMatch,
  label = 'Receiver Name',
  methods = paymentMethods,
}) => {
  const [suggestions, setSuggestions] = useState<InvoicesMemberResource[]>([]);

  const [isInpuFocused, setIsInputFocused] = useState(false);

  const receiver = useWatch({ control, name });

  const debouncedName = useDebounce(receiver, 400);

  const fetchMembers = () =>
    api.get<never, InvoicesMemberResource[]>('/v1/payments/members/search', {
      params: {
        'filter[search]': debouncedName,
      },
    });

  const { data: invoicesMembers, refetch } = useQuery<InvoicesMemberResource[]>(
    ['invoices_members', name],
    fetchMembers,
    {
      staleTime: 120 * 1000,
      enabled: false,
    },
  );

  useEffect(() => {
    if (debouncedName.length < 2 && !invoicesMembers?.length) {
      return setSuggestions([]);
    }
    refetch();
    invoicesMembers?.length && setSuggestions(invoicesMembers);
  }, [debouncedName, invoicesMembers, refetch]);

  const splitMatchedText = (str: string) => {
    const regExp = new RegExp(debouncedName, 'gi');
    return str.replace(regExp, (match) => `<span>${match}</span>`);
  };

  const setPaymentType = (suggestion: InvoicesMemberResource) => {
    if (setValue) {
      const stored = getMemberPaymentMethod(suggestion);
      setValue(
        'payment_method',
        methods.includes(stored)
          ? stored
          : memberWallets(suggestion, methods)[0] ?? PaymentMethod.Bank,
      );
      setValue('paypal_email', suggestion.paypal_email);
      setValue('payoneer_email', suggestion.payoneer_email);
    }
  };

  useEffect(() => {
    const matched = suggestions.find((user) => user.name === receiver);
    // Existing members are locked to their stored payment type, unless they
    // have an email for a wallet this form offers — then they can switch
    // between bank and that wallet.
    setShouldDesableType?.(
      !!matched && memberWallets(matched, methods).length === 0,
    );
    onMemberMatch?.(matched ?? null);
  }, [receiver, setShouldDesableType, onMemberMatch, suggestions, methods]);

  return (
    <AttachmentBox>
      <InputWrap>
        <Label htmlFor={name}>
          {label}
          <InfoTooltip
            width={270}
            title={label}
            content="Type the Receiver's Name, excluding all Lithuanian letters. If the receiver's information already exists in the system, suggestions will appear in the dropdown selection field."
            position="bottom-center"
          />
        </Label>
        <Controller
          control={control}
          name={name}
          render={({ field: { onChange, value }, fieldState: { error } }) => (
            <>
              <Input
                $hasError={!!error}
                onChange={(e) => {
                  onChange(e.target.value);
                }}
                placeholder="Add receiver"
                value={value}
                onFocus={() => setIsInputFocused(true)}
                onBlur={() => setTimeout(() => setIsInputFocused(false), 150)}
                onPaste={(e) => e.preventDefault()}
              />
              {!!suggestions.length && isInpuFocused && (
                <Autocomplete>
                  {suggestions.map((suggestion, index) => (
                    <AutocompleteButton
                      key={index}
                      type="button"
                      as="button"
                      onClick={(e: MouseEvent<HTMLButtonElement>) => {
                        e.stopPropagation();
                        onChange(suggestion.name);
                        setPaymentType(suggestion);
                        setIsInputFocused(false);
                      }}
                    >
                      {!!suggestion.banks?.length && <BankMark />}
                      {!!suggestion.paypal_email && <PaypalMark />}
                      {!!suggestion.payoneer_email && <PayoneerMark />}
                      <Text
                        $color="gray"
                        $type="captionSemibold"
                        dangerouslySetInnerHTML={{
                          __html: splitMatchedText(suggestion.name),
                        }}
                      />
                    </AutocompleteButton>
                  ))}
                </Autocomplete>
              )}
            </>
          )}
        />
      </InputWrap>
    </AttachmentBox>
  );
};

export default InvoicesRecieverField;
